import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, catchError, shareReplay, switchMap } from 'rxjs';
import { common_urls, common_urls_credit_card } from 'src/environments/environment';
import { CwsRedirectService } from 'src/app/service/cws-redirect.service';
import {
  EligiblePolicy,
  KanjiNameResponse,
  SubmitCardDetailsPayload,
  SubmitCardDetailsResponse,
} from './credit-card.interfaces';
import { EligiblePoliciesResponse } from './credit-card.interfaces';

@Injectable({
  providedIn: 'root',
})
export class CreditCardService {
  private static readonly SELECTED_POLICIES_STORAGE_KEY = 'credit-card:selected-policies';
  private httpClient = inject(HttpClient);
  private oidcSecurityService = inject(OidcSecurityService);
  private cwsRedirectService = inject(CwsRedirectService);
  policies?: EligiblePolicy[];

  private selectedPolicies: EligiblePolicy[] = [];
  private kanjiName$?: Observable<KanjiNameResponse>;
  private eligiblePolicies$?: Observable<EligiblePoliciesResponse>;
  isProceedConfirmed = false;

  /** Store the full policy objects selected by the user. */
  setSelectedPolicies(policies: EligiblePolicy[]): void {
    this.selectedPolicies = [...policies];
    this.saveSelectedPoliciesToSessionStorage(this.selectedPolicies);
  }

  /** Retrieve the previously stored selected policies. */
  getSelectedPolicies(): EligiblePolicy[] {
    if (!this.selectedPolicies.length) {
      this.selectedPolicies = this.readSelectedPoliciesFromSessionStorage();
    }
    return [...this.selectedPolicies];
  }

  /** Clear selected policies from in-memory state and session storage. */
  clearSelectedPolicies(): void {
    this.selectedPolicies = [];
    this.removeSelectedPoliciesFromSessionStorage();
  }

  getKanjiName(): Observable<KanjiNameResponse> {
    if (!this.kanjiName$) {
      const apiEndpoint = `${common_urls_credit_card.bffBaseUrl}/user-management/userdetails/getkanjiname`;
      this.kanjiName$ = this.oidcSecurityService
        .getAccessToken()
        .pipe(
          switchMap((accessToken) =>
            this.httpClient.get<KanjiNameResponse>(apiEndpoint, this.getRequestOptions(accessToken))
          ),
          shareReplay(1),
          catchError((error) => {
            this.kanjiName$ = undefined; // allow retry on failure
            this.cwsRedirectService.redirectToCWSErrorPage();
            throw Error(error);
          })
        );
    }
    return this.kanjiName$;
  }

  submitCardDetails(payload: SubmitCardDetailsPayload): Observable<SubmitCardDetailsResponse> {
    const apiEndpoint = `${common_urls_credit_card.bffBaseUrl}/credit-card-change/card-details/submit`;
    return this.oidcSecurityService
      .getAccessToken()
      .pipe(
        switchMap((accessToken) =>
          this.httpClient.post<SubmitCardDetailsResponse>(
            apiEndpoint,
            payload,
            this.getRequestOptions(accessToken))
        ),
        catchError((error) => {
          this.cwsRedirectService.redirectToCWSErrorPage();
          throw Error(error);
        })
      );
  }

  private getRequestOptions(accessToken: string): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
      }),
    };
  }

  private saveSelectedPoliciesToSessionStorage(policies: EligiblePolicy[]): void {
    try {
      sessionStorage.setItem(CreditCardService.SELECTED_POLICIES_STORAGE_KEY, JSON.stringify(policies));
    } catch {
      // Ignore storage errors and continue with in-memory fallback.
    }
  }

  private readSelectedPoliciesFromSessionStorage(): EligiblePolicy[] {
    try {
      const stored = sessionStorage.getItem(CreditCardService.SELECTED_POLICIES_STORAGE_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed as EligiblePolicy[] : [];
    } catch {
      return [];
    }
  }

  private removeSelectedPoliciesFromSessionStorage(): void {
    try {
      sessionStorage.removeItem(CreditCardService.SELECTED_POLICIES_STORAGE_KEY);
    } catch {
      // Ignore storage errors.
    }
  }

  
}